<template>
    <section class="section is-full-height-with-navbar">
        <div class="container" v-if="initDone">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("page.admin_edit_event.title") }}</h1>
                </div>
            </div>

            <!-- Event Status Indicator -->
            <div class="columns">
                <div class="column is-flex is-align-items-center is-flex-wrap-wrap is-gap-0.5">
                    <EventStatusIndicator />

                    <template v-if="eventPageUrl">
                        <a class="button is-small" :href="eventPageUrl" target="_blank">
                            <span class="icon"><i class="fas fa-eye"></i></span>
                            <span>{{ translate("page.admin_edit_event.view_event_page") }}</span>
                        </a>
                        <button class="button is-small" @click="copyEventPageUrl">
                            <span class="icon"><i class="fas fa-copy"></i></span>
                            <span>{{ translate("live_scoreboards.copy_link") }}</span>
                        </button>
                    </template>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <div class="tabs is-boxed">
                        <ul>
                            <li :class="showTab === 'main' ? 'is-active' : ''">
                                <a @click="changeTab('main')">
                                    {{ translate("page.admin.edit_event.tab.main") }}
                                </a>
                            </li>
                            <li :class="showTab === 'registrationStages' ? 'is-active' : ''">
                                <a @click="changeTab('registrationStages')">
                                    {{ translate("wizard.registration.options") }}
                                </a>
                            </li>
                            <li :class="showTab === 'images' ? 'is-active' : ''">
                                <a @click="changeTab('images')">
                                    {{ translate("page.admin.edit_event.tab.images") }}
                                </a>
                            </li>
                            <li id="edit-event-internal-info-tab" :class="showTab === 'internalInfo' ? 'is-active' : ''">
                                <a @click="changeTab('internalInfo')">
                                    {{ translate("page.admin.edit_event.tab.internal_information") }}
                                </a>
                            </li>
                            <li :class="showTab === 'eventFiles' ? 'is-active' : ''">
                                <a @click="changeTab('eventFiles')">
                                    {{ translate("page.admin_edit_event.event_files") }}
                                </a>
                            </li>
                            <li id="edit-event-settings-tab" :class="showTab === 'settings' ? 'is-active' : ''">
                                <a @click="changeTab('settings')">
                                    {{ translate("admin.menu.event.edit_settings") }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div v-if="showTab === 'main'">
                <MainTab />
            </div>
            <div v-if="showTab === 'registrationStages'">
                <RegistrationStagesTab />
            </div>
            <div class="top-buffer-10" v-if="showTab === 'images'">
                <EventImagesTab @imageChanged="fetchEvent" />
            </div>
            <div class="top-buffer-10" v-if="showTab === 'internalInfo'">
                <InternalInformationTab ref="internalInformationTab" />
            </div>
            <div v-if="showTab === 'eventFiles'">
                <EventFilesTab />
            </div>
            <div v-if="showTab === 'settings'">
                <SettingsTab :event-id="props.eventId" />
            </div>

            <div class="columns">
                <div class="column">
                    <div
                        class="buttons"
                        v-if="
                            showTab !== 'images' && showTab !== 'eventFiles' && showTab !== 'registrationStages' && showTab !== 'settings'
                        "
                    >
                        <LoadingButton :is-loading="savingEventInProgress" class="button is-primary" @click="updateEvent">
                            <span class="icon">
                                <i class="far fa-save"></i>
                            </span>
                            <span>{{ translate("common.save") }}</span>
                        </LoadingButton>
                        <LoadingButton :is-loading="reloadingInProgress" class="button" @click="reloadEventData(true)">
                            <span class="icon"><i class="fas fa-times"></i> </span>
                            <span>{{ translate("form.reset_form") }}</span>
                        </LoadingButton>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="container">
            <div class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </div>

        <EventLocationModal ref="eventLocationModal" />

        <ContactInfoModal ref="contactInfoModal" />

        <EventStatusModal ref="eventStatusModal" :current-user-id="props.currentUserId" />

        <WelcomeOnboardingModal ref="welcomeOnboardingModal" />
    </section>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, nextTick, defineAsyncComponent } from "vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import MainTab from "./EditEvent/MainTab.vue";
    import EventImagesTab from "./EditEvent/EventImagesTab.vue";
    import history from "../Common/history";
    import LoadingButton from "../Common/LoadingButton.vue";
    import EventFilesTab from "./EditEvent/EventFilesTab.vue";
    import RegistrationStagesTab from "./EditEvent/RegistrationStagesTab.vue";
    import SettingsTab from "./EditEvent/SettingsTab.vue";
    import EventLocationModal from "./EditEvent/EventLocationModal.vue";
    import { CONTACT_INFO_OPEN, EVENT_LOCATION_OPEN, EVENT_STATUS_OPEN, eventBus } from "./eventBus";
    import ContactInfoModal from "./ContactInfoModal.vue";
    import EventStatusModal from "./EditEvent/EventStatusModal.vue";
    import WelcomeOnboardingModal from "./EditEvent/WelcomeOnboardingModal.vue";
    import EventStatusIndicator from "./EditEvent/EventStatusIndicator.vue";
    import { useEditEventStore } from "./editEventStore";
    import { Event, FullEvent, MultiFieldValidationErrors } from "../Common/types";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { useFeatureAnnouncement } from "../Common/FeatureAnnouncements/useFeatureAnnouncement";
    import { useScrollResetOnMount } from "../Common/useScrollResetOnMount";

    useScrollResetOnMount();

    // Lazy-loaded: Tiptap adds non-trivial bundle weight, and this tab is opened far less
    // often than the others on this page — no need to pay for it on every event-edit page load.
    const InternalInformationTab = defineAsyncComponent({
        loader: () => import("./EditEvent/InternalInformationTab.vue"),
        loadingComponent: LoadingSpinner2,
    });

    const props = defineProps<{
        eventId: number;
        organizerPhone: string | null;
        currentUserId?: number | null;
    }>();

    const historyInstance = history.getInstance(window);
    const store = useEditEventStore();
    store.eventId = props.eventId;
    store.organizerPhone = props.organizerPhone;

    const showTab = ref<string>("main");
    const eventPageUrl = ref<string>("");
    const savingEventInProgress = ref<boolean>(false);
    const reloadingInProgress = ref<boolean>(false);

    function copyEventPageUrl() {
        navigator.clipboard.writeText(eventPageUrl.value).then(() => {
            successToast(translate("invitation_links.copied_to_clipboard"));
        });
    }

    const eventLocationModal = ref<ComponentExposed<typeof EventLocationModal> | null>(null);
    const contactInfoModal = ref<ComponentExposed<typeof ContactInfoModal> | null>(null);
    const eventStatusModal = ref<ComponentExposed<typeof EventStatusModal> | null>(null);
    const welcomeOnboardingModal = ref<ComponentExposed<typeof WelcomeOnboardingModal> | null>(null);
    const internalInformationTab = ref<{ resetContent: () => Promise<void> } | null>(null);

    const mainEvent = computed({
        get: () => store.event,
        set: (value: Event) => store.updateEvent(value),
    });

    const validationErrors = computed({
        get: () => store.validationErrors,
        set: (value: MultiFieldValidationErrors) => (store.validationErrors = value),
    });

    const eventTypes = computed(() => store.eventTypes);

    const changeTab = (tabName: string) => {
        // Guard: prevent switching tabs if there are unsaved changes in main tab
        if (showTab.value === "main" && tabName !== "main" && store.changed) {
            const save = confirm(translate("page.admin_edit_event.unsaved_changes_prompt"));

            if (save) {
                // User chose to save changes
                savingEventInProgress.value = true;
                updateEvent()
                    .then(() => {
                        store.changed = false;
                        switchToTab(tabName);
                    })
                    .finally(() => {
                        savingEventInProgress.value = false;
                    });
            } else {
                // User chose to discard changes
                reloadEventData(false).then(() => {
                    switchToTab(tabName);
                });
            }
            return;
        }

        // Guard: prevent switching tabs if there are unsaved changes in internal info tab
        if (showTab.value === "internalInfo" && tabName !== "internalInfo" && store.changed) {
            const save = confirm(translate("page.admin_edit_event.unsaved_changes_prompt"));

            if (save) {
                // User chose to save changes
                updateEvent().then(() => {
                    switchToTab(tabName);
                });
            } else {
                // User chose to discard changes
                reloadEventData(false).then(() => {
                    switchToTab(tabName);
                });
            }
            return;
        }

        // No unsaved changes, proceed with tab change
        switchToTab(tabName);
    };

    function switchToTab(tabName: string) {
        historyInstance.setQueryParam("tab", tabName);
        showTab.value = tabName;
    }

    function updateEvent() {
        savingEventInProgress.value = true;

        // Prepare event data based on current tab
        const eventData = showTab.value === "main" ? withGeoLocation() : store.event;
        const eventTypesToSave = eventTypes.value.map(et => et.key);

        return store
            .saveEvent(eventData, eventTypesToSave)
            .then(updatedEvent => {
                successToast(translate("common.changes_have_been_saved"));
                validationErrors.value = {};
                eventPageUrl.value = generatePublicEventPageUrl(updatedEvent.event);
            })
            .catch(handleUpdateError)
            .finally(() => {
                savingEventInProgress.value = false;
            });
    }

    const withGeoLocation = (): Event => {
        // @ts-expect-error - latitude and longitude are added dynamically //TODO: Get rid of it and keep only geoLocation field
        const lat = mainEvent.value.latitude || "";
        // @ts-expect-error - latitude and longitude are added dynamically
        const lng = mainEvent.value.longitude || "";

        return {
            ...mainEvent.value,
            geoLocation: lat + "," + lng,
        };
    };

    const fetchEvent = (): Promise<FullEvent> => {
        return store.fetchFullEvent().then(fullEvent => {
            eventPageUrl.value = generatePublicEventPageUrl(fullEvent.event);
            return fullEvent;
        });
    };

    const handleUpdateError = (err: any) => {
        if (err.validationErrors) {
            updateValidationErrors(err.validationErrors);
        }

        handleError(err);
    };

    const updateValidationErrors = (validationErrorsToUpdate: Array<{ field: string; message: string[] }>) => {
        const convertedToMap = validationErrorsToUpdate.reduce(
            (acc, item) => {
                acc[item.field] = item.message;
                return acc;
            },
            {} as Record<string, string[]>,
        );

        store.validationErrors = convertedToMap;
    };

    const reloadEventData = (askToConfirm: boolean): Promise<void> => {
        if (askToConfirm) {
            if (!confirm(translate("common.are_you_sure"))) {
                return Promise.resolve();
            }
        }

        reloadingInProgress.value = true;

        return fetchEvent()
            .then(() => {
                successToast(translate("common.changes_discarded"));
                store.validationErrors = {};

                // The Tiptap editor never re-syncs its own content from the store reactively
                // (to avoid cursor-jump bugs), so an explicit reset is needed after a refetch.
                if (showTab.value === "internalInfo") {
                    return internalInformationTab.value?.resetContent();
                }

                return undefined;
            })
            .catch(handleError)
            .finally(() => {
                reloadingInProgress.value = false;
            });
    };

    const generatePublicEventPageUrl = (event: Event): string => {
        // A blank slug is a validation error surfaced on the "URL slug" field (see editEventStore),
        // not something to paper over with a guessed URL here.
        if (!event.urlSlug?.trim()) {
            return "";
        }

        return `${window.location.origin}/events/${event.id}-${event.urlSlug}`;
    };

    const initDone = ref(false);

    onMounted(() => {
        initDone.value = false;

        eventBus.$on(EVENT_LOCATION_OPEN, () => {
            eventLocationModal.value?.open();
        });
        eventBus.$on(CONTACT_INFO_OPEN, () => {
            contactInfoModal.value?.open();
        });
        eventBus.$on(EVENT_STATUS_OPEN, () => {
            eventStatusModal.value?.open();
        });

        const currentTab = historyInstance.getQueryParam("tab");
        if (currentTab) {
            changeTab(currentTab);
        }

        fetchEvent()
            .then(() => {
                nextTick(() => {
                    store.changed = false;
                });

                const action = historyInstance.getQueryParam("action");
                if (action === "publish") {
                    historyInstance.setQueryParam("action", null);
                    nextTick(() => eventStatusModal.value?.open());
                } else if (action === "verifyOrganization") {
                    historyInstance.setQueryParam("action", null);
                    nextTick(() => eventStatusModal.value?.open("billing"));
                } else if (action === "onboarding") {
                    const isFreshFederation = store.metadata?.federationApproved === false;
                    const alreadyDecided = store.metadata?.postWizardOnboardingDecision !== null;
                    if (isFreshFederation && !alreadyDecided) {
                        // Query param is stripped by WelcomeOnboardingModal once the organizer
                        // makes a decision (skip or start tour), not immediately on mount.
                        nextTick(() => welcomeOnboardingModal.value?.open());
                    } else {
                        historyInstance.setQueryParam("action", null);
                    }
                }
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;

                if (props.currentUserId) {
                    nextTick(() => {
                        useFeatureAnnouncement().showEligible(props.currentUserId as number, [
                            { key: "edit_event_internal_information_visual_editor", element: "#edit-event-internal-info-tab" },
                            { key: "edit_event_settings_tab", element: "#edit-event-settings-tab" },
                        ]);
                    });
                }
            });
    });
</script>

<style></style>
